import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  X,
  Lock,
  KeyRound,
  Loader2,
} from 'lucide-react';
import { DevLogin } from './DevLogin';
import { PortalAuthService } from '../services/auth-service';
import { useVerifyOTP } from '../hooks';
import { Button } from '@/components/ui/button';
import { buttonVariants } from '@/components/ui/button-variants';

interface MagicLinkLoginProps {
  onSuccess?: () => void;
}

// Check if we're in development mode
const isDevelopment = import.meta.env.DEV;

export function MagicLinkLogin({ onSuccess }: MagicLinkLoginProps) {
  const navigate = useNavigate();

  // Check for sessionExpired query parameter
  const sessionExpired = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('sessionExpired') === 'true';
  }, []);

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDevLogin, setShowDevLogin] = useState(false);
  const [email, setEmail] = useState('');
  const [showOTPInput, setShowOTPInput] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);

  const handleOTPSuccess = () => {
    onSuccess?.();
    navigate('/portal/select-investor');
  };

  const { mutate: verifyOTP, isPending: otpLoading } =
    useVerifyOTP(handleOTPSuccess);

  const onSubmit = async (e: React.SubmitEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Send magic link - investor selection happens after authentication now
      await PortalAuthService.sendMagicLink(email);
      setIsSubmitted(true);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOTPSubmit = () => {
    setOtpError(null);
    verifyOTP(
      { email, code: otpCode },
      {
        onError: (err) => {
          setOtpError(
            err instanceof Error ? err.message : 'Failed to verify code'
          );
        },
      }
    );
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-foreground">Dff Flow</h1>
        </div>

        <div className="w-full max-w-md border border-border bg-card rounded-2xl p-8">
          <div className="text-center space-y-6">
            <h2 className="text-center text-2xl font-semibold text-foreground">
              Check your email
            </h2>

            <p className="text-sm text-muted-foreground leading-relaxed">
              We&apos;ve sent you a temporary login link.
              <br />
              Please check your inbox at{' '}
              <span className="font-medium text-foreground">{email}</span>.
            </p>

            {!showOTPInput && (
              <Button
                variant="outline"
                onClick={() => setShowOTPInput(true)}
                className="w-full flex items-center justify-center"
              >
                <KeyRound className="h-5 w-5" />
                Enter code manually
              </Button>
            )}

            {showOTPInput && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Enter the 6-digit code from your email
                </p>
                <input
                  type="text"
                  inputMode="numeric"
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="000000"
                  maxLength={6}
                  className="w-full border border-input bg-background rounded-xl py-4 px-5 text-foreground text-center text-2xl tracking-[0.3em] font-mono placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors"
                  disabled={otpLoading}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && otpCode.length === 6) {
                      e.preventDefault();
                      handleOTPSubmit();
                    }
                  }}
                />
                {otpError && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-destructive">{otpError}</p>
                  </div>
                )}
                <button
                  type="button"
                  onClick={handleOTPSubmit}
                  disabled={otpCode.length !== 6 || otpLoading}
                  className={buttonVariants({
                    variant: 'portal',
                    size: 'portal',
                  })}
                >
                  {otpLoading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <KeyRound className="h-5 w-5" />
                      Verify code
                    </>
                  )}
                </button>
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsSubmitted(false);
                setShowOTPInput(false);
                setOtpCode('');
                setOtpError(null);
              }}
              className="w-full flex items-center justify-center"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Show development login if enabled
  if (isDevelopment && showDevLogin) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="w-full max-w-md space-y-4">
          <DevLogin onSuccess={onSuccess} />
          <button
            className="w-full py-3 font-medium text-muted-foreground border border-border rounded-full hover:border-foreground/40 transition-colors"
            onClick={() => setShowDevLogin(false)}
          >
            Back to Magic Link Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="w-full max-w-md border border-border bg-card rounded-2xl p-8 relative">
        {/* Close Button */}
        <Link
          to="/"
          className="absolute top-6 right-6 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="h-6 w-6" />
        </Link>

        {/* Header */}
        <div className="text-center space-y-3 mb-8">
          <h2 className="text-2xl font-semibold text-foreground">
            Sign in to your account
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Enter your email address to access your
            <br />
            investor portal
          </p>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-6">
          {sessionExpired && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">
                Your session has expired. Please sign in again to continue.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <label
              htmlFor="email"
              className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground"
            >
              Email Address
            </label>
            <div className="relative">
              <input
                id="email"
                type="email"
                placeholder="investor@global-lp.com"
                className="w-full border border-input bg-background rounded-xl py-4 px-5 pr-12 text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring transition-colors"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
              <Lock className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            </div>
          </div>

          {error && (
            <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className={buttonVariants({ variant: 'portal', size: 'portal' })}
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent" />
                Sending...
              </>
            ) : (
              <>
                Send login link
                <ArrowRight className="h-5 w-5" />
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground leading-relaxed">
            Only authorized investor contacts can access this
            <br />
            portal. Contact your fund manager if you need
            <br />
            assistance.
          </p>

          {isDevelopment && (
            <div className="mt-8 space-y-4">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-card px-4 text-muted-foreground font-medium tracking-wider">
                    Development Only
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="w-full py-3 font-medium text-muted-foreground border border-border rounded-full hover:border-foreground/40 transition-colors"
                onClick={() => setShowDevLogin(true)}
              >
                Development Login
              </button>
              <p className="text-xs text-muted-foreground">
                Skip magic link for development
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
